import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useStore } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { debounce } from 'lodash';
import {
    DEFAULT_AUTOSAVE_DEBOUNCE_MS,
    FORM_INIT_DELAY_MS,
    POST_RESTORE_DELAY_MS,
    AutoSaveStoredData
} from 'src/constants/autoSave';
import { safeExecute, safeSetStorage, safeClearStorage } from 'src/utils/autoSaveErrorHandler';

export interface UseAutoSaveOptions {
    /**
     * Unique key for storing form data
     * Must be non-empty for auto-save to function
     */
    storageKey: string;

    /**
     * Delay in milliseconds before saving after user stops typing
     * @default 500
     */
    debounceMs?: number;

    /**
     * Whether auto-save is enabled
     * Set to false to temporarily disable auto-save
     * @default true
     */
    enabled?: boolean;
}

/**
 * Hook for implementing auto-save functionality using React Admin's store
 *
 * @param { UseAutoSaveOptions } options - Configuration options for auto-save
 * @returns Object with methods to manually control auto-save
 *
 * @example
 * ```tsx
 * const { clearAutoSave } = useAutoSave({
 *   storageKey: 'employee-add-form',
 *   debounceMs: 500 // Optional, defaults to 500ms
 * });
 *
 * // Clear auto-saved data on successful submit
 * const onSuccess = () => {
 *   clearAutoSave();
 * };
 * ```
 */
export const useAutoSave = (options: UseAutoSaveOptions) => {
    const { storageKey, debounceMs = DEFAULT_AUTOSAVE_DEBOUNCE_MS, enabled = true } = options;

    const { watch, reset, getValues } = useFormContext();
    const isRestoringRef = useRef(false);
    const hasRestoredRef = useRef(false);

    // Disable auto-save if no valid storage key (e.g., waiting for record to load in edit mode)
    const isEnabled = enabled && !!storageKey;

    const [storedData, setStoredData] = useStore<AutoSaveStoredData | undefined>(storageKey, undefined);

    const saveToStorage = useCallback(
        (data: Record<string, unknown>) => {
            if (!isEnabled || isRestoringRef.current) return;

            const timestamp = new Date().toISOString();

            safeSetStorage(setStoredData, { data, timestamp }, 'Failed to save form data');
        },
        [isEnabled, setStoredData]
    );

    const debouncedSave = useMemo(
        () => debounce((data: Record<string, unknown>) => saveToStorage(data), debounceMs),
        [saveToStorage, debounceMs]
    );

    const restoreFromStorage = useCallback(() => {
        if (!isEnabled || hasRestoredRef.current || !storedData) return;

        const result = safeExecute(() => {
            const { data } = storedData;

            isRestoringRef.current = true;
            reset(data);
            hasRestoredRef.current = true;

            setTimeout(() => {
                isRestoringRef.current = false;
            }, POST_RESTORE_DELAY_MS);
        }, 'Failed to restore form data');

        // If restoration failed, attempt to clear corrupt data
        if (!result.success) {
            safeClearStorage(setStoredData, 'Clear corrupt data after restore failure');
        }
    }, [isEnabled, storedData, reset, setStoredData]);

    const clearAutoSave = useCallback(() => {
        safeClearStorage(setStoredData, 'Clear auto-save data');
    }, [setStoredData]);

    const manualSave = useCallback(() => {
        const data = getValues();
        saveToStorage(data);
    }, [getValues, saveToStorage]);

    // Restore data on mount or mark as ready for auto-save
    useEffect(() => {
        if (!isEnabled) return;

        // Only run this once
        if (hasRestoredRef.current) return;

        // Delay to ensure form is fully initialized before restoration
        const timer = setTimeout(() => {
            if (storedData) {
                // We have data to restore
                restoreFromStorage();
            } else {
                // No data to restore, but mark as ready for auto-save
                hasRestoredRef.current = true;
            }
        }, FORM_INIT_DELAY_MS);

        return () => clearTimeout(timer);
    }, [isEnabled]); // Only run once on mount or when storage key becomes available

    // Watch form changes and auto-save
    useEffect(() => {
        if (!isEnabled || !watch) return;

        try {
            const subscription = watch(() => {
                if (!isRestoringRef.current && hasRestoredRef.current) {
                    const currentData = getValues();
                    debouncedSave(currentData);
                }
            });

            return () => {
                subscription.unsubscribe();
                debouncedSave.cancel();
            };
        } catch (error) {
            // Form watch not ready yet, skip this render
            return;
        }
    }, [watch, debouncedSave, isEnabled, getValues]);

    // Save before page unload
    useEffect(() => {
        if (!isEnabled) return;

        const handleBeforeUnload = () => {
            const data = getValues();
            saveToStorage(data);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isEnabled, getValues, saveToStorage]);

    return {
        clearAutoSave,
        manualSave,
        restoreFromStorage
    };
};

export default useAutoSave;
